import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Tree, type TreeProps } from 'antd'
import { type EventDataNode } from 'antd/es/tree'
import { type DataNode } from 'antd/es/tree'
import { IconCaretRight } from '@vtex/admin-ui'
import {
  type Category,
  CATEGORY_ENDPOINT,
  useCategory,
} from '@/hooks/useCategory'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { fetcher } from '@/lib/fetcher'
import { appendChildrenToTree } from '@/lib/tree'

import { FilterLoading } from '../../filters/filters'

import * as styles from './CategoryTree.styles'

const convertCategoriesToTreeData = (categories: Category[] = []) =>
  categories.map?.((category) => ({
    ...category,
    title: category.name,
    key: category.id,
    isLeaf: !category.hasChildren,
  }))

type CategoryTreeProps = {
  categoryName?: string
  onChange: (activeItems: DataNode[]) => void
  initialCheckedKeys?: number[]
}

export const CategoryTree = ({
  categoryName,
  onChange,
  initialCheckedKeys = [],
}: CategoryTreeProps) => {
  const [categoriesTreeDataWithChildren, setCategoriesTreeDataWithChildren] =
    useState<DataNode[]>([])
  const [checkedKeys, setCheckedKeys] = useState<number[]>(initialCheckedKeys)
  const [idsToLoad, setIdsToLoad] = useLocalStorage<number[]>(
    'adminCatalogCategoryIdsToLoaderFilter',
    []
  )

  const { data: categories = [], isLoading } = useCategory({
    categoryName,
  })

  const onCheck: TreeProps['onCheck'] = (keys, infos) => {
    onChange(infos.checkedNodes as DataNode[])
    setIdsToLoad(infos.halfCheckedKeys as number[])
    setCheckedKeys(keys as number[])
  }

  const categoriesTreeData = useMemo(() => {
    if (isLoading) return []

    return convertCategoriesToTreeData(categories)
  }, [categories, isLoading])

  const onLoadData = useCallback(
    async ({ key, children }: EventDataNode<DataNode>) => {
      if (children) {
        return
      }

      try {
        const data = await fetcher({
          url: `${CATEGORY_ENDPOINT}?parentId=${key}`,
        })

        setCategoriesTreeDataWithChildren((origin) =>
          appendChildrenToTree(
            origin.length > 0 ? origin : categoriesTreeData,
            key,
            convertCategoriesToTreeData(data),
            'key'
          )
        )
      } catch (_) {}
    },
    [categoriesTreeData]
  )

  useEffect(() => {
    if (idsToLoad.length > 0) {
      const loadIds = async () => {
        for (const id of idsToLoad) {
          await onLoadData({ key: id } as EventDataNode<DataNode>)
        }
      }
      loadIds()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onLoadData])

  if (isLoading) {
    return <FilterLoading />
  }

  return (
    <Tree
      checkable
      loadData={onLoadData}
      treeData={
        categoriesTreeDataWithChildren.length > 0
          ? categoriesTreeDataWithChildren
          : categoriesTreeData
      }
      onCheck={onCheck}
      selectable={false}
      rootClassName={styles.wrapper}
      switcherIcon={<IconCaretRight size="regular" />}
      checkedKeys={checkedKeys}
      virtual
    />
  )
}
